import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

import * as Sentry from "@sentry/react";
import ReactGA from "react-ga4";
import { clarity } from "react-microsoft-clarity";
import "./index.css";
import "react-photo-view/dist/react-photo-view.css";
import { type AuthContextType, AuthProvider, useAuth } from "./providers/auth";

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPendingMinMs: 0,
  defaultPreload: "intent",
  scrollRestoration: true,
  context: {
    auth: null! as AuthContextType, // Type assertion since we'll provide this via context
  },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,

  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
  sendDefaultPii: true,

  integrations: [
    // If you're using react router, use the integration for your react router version instead.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.httpClientIntegration({
      // capture every 4xx & 5xx, not just 5xx (default)
      failedRequestStatusCodes: [[400, 599]],
      // capture only our back-end hosts
      failedRequestTargets: [
        /^https?:\/\/localhost:8000\//,
        /^https:\/\/app\.openfarming\.earth\//,
      ],
    }),
    Sentry.captureConsoleIntegration({
      levels: ["error", "warn"], // add 'log' / 'info' if you want those too
    }),
  ],

  // Enable logs to be sent to Sentry
  _experiments: { enableLogs: true },

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 1.0,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [
    /^\//, // Same-origin (relative) URLs
    /^https?:\/\/localhost(?::\d{1,5})?\//, // http(s)://localhost:PORT/
    /^https?:\/\/127\.0\.0\.1(?::\d{1,5})?\//, // http(s)://127.0.0.1:PORT/
    /^https:\/\/api\.openfarming\.earth\//, // https://app.openfarming.earth/
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

ReactGA.initialize(import.meta.env.VITE_GA4_ID);
clarity.init(import.meta.env.VITE_MS_CLARITY_ID);
clarity.consent();
clarity.start();

function InnerApp() {
  const auth = useAuth();
  return (
    <RouterProvider
      router={router}
      defaultPreload="intent"
      context={{ auth }}
    />
  );
}

function App() {
  return (
    <AuthProvider>
      <InnerApp />
    </AuthProvider>
  );
}

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
